/**
 * THE SHARED LIST — the page a person opens from a text or an email.
 *
 * No account, no login, nothing to tap but the language. It is READ ONLY: no
 * add, no quantity, no tick, no bin. It shows the list as it is right now,
 * grouped by aisle, with the choices and the note, in the READER's language,
 * and it says when the list last changed.
 *
 * It shows the sender's first name and nothing else about the household: no
 * address, no contact, no billing, no other names.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Clock, Loader2, ShoppingBasket } from 'lucide-react';import LanguageToggle from '../../components/LanguageToggle';
import GroceryModeToggle from '../../components/grocery/GroceryModeToggle';
import GroceryIcon from '../../components/grocery/GroceryIcon';
import { BrandPattern } from '../../components/grocery/BrandPattern';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
const POLL_MS = 30000;

export default function SharedListPage() {
    const { token } = useParams();
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await fetch(`${API}/api/grocery/shared/${token}?lang=${lang}`);
            if (!r.ok) throw new Error(`http_${r.status}`);
            setData(await r.json());
            setFailed(false);
        } catch (_e) { setFailed(true); }
    }, [token, lang]);

    useEffect(() => { load(); const id = setInterval(load, POLL_MS); return () => clearInterval(id); },
        [load]);

    /** THE TOKEN IS IN THIS URL, SO THE PAGE MUST NOT LEAK IT OR BE LISTED.
     *  No search engine, no Referer header on anything the reader taps. The
     *  tags are removed again when the reader leaves this page. */
    useEffect(() => {
        const tags = [['robots', 'noindex, nofollow, noarchive, nosnippet'],
            ['referrer', 'no-referrer']].map(([name, content]) => {
            const m = document.createElement('meta');
            m.setAttribute('name', name);
            m.setAttribute('content', content);
            m.setAttribute('data-shared-list', 'true');
            document.head.appendChild(m);
            return m;
        });
        return () => tags.forEach((m) => m.remove());
    }, []);

    if (!data) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 dark:bg-slate-950">
                {failed ? (
                    <>
                        <p className="text-sm font-semibold" data-testid="shared-list-load-failed">
                            {t('grocery.couldNotLoad')}
                        </p>
                        <button onClick={load} data-testid="shared-list-retry"
                            className="min-h-[44px] rounded-full bg-emerald-700 px-5 text-sm font-bold text-white">
                            {t('grocery.tryAgain')}
                        </button>
                    </>
                ) : <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />}
            </div>
        );
    }

    if (data.state !== 'active') {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 px-6 text-center dark:bg-slate-950"
                data-testid="shared-list-gone">
                <ShoppingBasket className="h-10 w-10 text-slate-400" />
                <p className="text-lg font-bold text-foreground">{t('grocery.sharing.goneTitle')}</p>
                <p className="max-w-sm text-sm text-muted-foreground">{t('grocery.sharing.goneLine')}</p>
            </div>
        );
    }

    const groups = [];
    (data.items || []).forEach((i) => {
        const g = groups.find((x) => x.key === i.category);
        if (g) g.items.push(i);
        else groups.push({ key: i.category, name: i.category_name, items: [i] });
    });
    const when = data.updated_at
        ? new Date(data.updated_at).toLocaleString(lang,
            { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
        : '';

    return (
        <div className="min-h-screen bg-slate-50 pb-16 text-[#13265C] dark:bg-slate-950 dark:text-slate-100"
            data-testid="shared-list-page">
            <BrandPattern pattern={data.brand_pattern} />
            <div className="relative z-10 mx-auto w-full max-w-2xl px-4 py-5 sm:px-6">
                <header className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h1 className="text-2xl font-bold sm:text-3xl"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                            data-testid="shared-list-title">
                            {t('grocery.sharing.pageTitle', { name: data.sender_name })}
                        </h1>
                        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"
                            data-testid="shared-list-updated">
                            <Clock className="h-4 w-4 shrink-0" />
                            {t('grocery.sharing.updated', { when })}
                        </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <LanguageToggle />
                        <GroceryModeToggle memberKey="shared" testId="shared-list-mode" compact />
                    </div>
                </header>

                <p className="mt-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-muted-foreground dark:border-slate-700 dark:bg-slate-900"
                    data-testid="shared-list-readonly-note">
                    {t('grocery.sharing.readOnly')}
                </p>

                {!(data.items || []).length ? (
                    <p className="py-16 text-center text-sm text-muted-foreground"
                        data-testid="shared-list-empty">{t('grocery.sharing.emptyList')}</p>
                ) : null}

                <div className="mt-4 space-y-4">
                    {groups.map((g) => (
                        <section key={g.key} data-testid={`shared-list-aisle-${g.key}`}
                            className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                                {g.name}
                            </p>
                            <ul className="mt-2 space-y-2">
                                {g.items.map((i) => (
                                    <li key={i.ref} className="flex items-start gap-2"
                                        data-testid={`shared-list-item-${i.ref}`}>
                                        <GroceryIcon icon={i.icon} badge={i.badge} size={28}
                                            alt="" />
                                        <span className="min-w-0 flex-1">
                                            <span className="block break-words text-base font-semibold">
                                                {i.name}
                                                {i.qty > 1 ? (
                                                    <span className="ml-1 text-sm font-bold text-emerald-700 dark:text-emerald-300">
                                                        × {i.qty}
                                                    </span>
                                                ) : null}
                                            </span>
                                            {(i.options || []).length ? (
                                                <span className="block break-words text-sm text-muted-foreground"
                                                    data-testid={`shared-list-options-${i.ref}`}>
                                                    {i.options.join(' · ')}
                                                </span>
                                            ) : null}
                                            {i.note ? (
                                                <span className="block break-words text-sm font-semibold text-sky-700 dark:text-sky-300"
                                                    data-testid={`shared-list-note-${i.ref}`}>
                                                    {i.note}
                                                </span>
                                            ) : null}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
}
